import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { fetchSeoMeta } from "../../lib/seoMeta";

// Dependency-free head manager: fetches computed SEO for the current path from
// the backend engine and imperatively syncs document <head> (title, meta,
// canonical, Open Graph, Twitter, JSON-LD). Mounted once inside PublicLayout so
// every public page automatically gets correct, up-to-date SEO — no per-page work.

function setMeta(attr, key, content) {
  if (!content) {
    document.head.querySelectorAll(`meta[${attr}="${key}"][data-seo]`).forEach((n) => n.remove());
    return;
  }
  let el = document.head.querySelector(`meta[${attr}="${key}"][data-seo]`);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, key);
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function setCanonical(href) {
  let el = document.head.querySelector('link[rel="canonical"][data-seo]');
  if (!href) { if (el) el.remove(); return; }
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", "canonical");
    el.setAttribute("data-seo", "1");
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function setJsonLd(obj) {
  document.head.querySelectorAll('script[type="application/ld+json"][data-seo]').forEach((n) => n.remove());
  if (!obj) return;
  const s = document.createElement("script");
  s.type = "application/ld+json";
  s.setAttribute("data-seo", "1");
  s.textContent = JSON.stringify(obj);
  document.head.appendChild(s);
}

export function applySeo(seo, siteName = "Clacton Fireworks") {
  if (!seo) return;
  if (seo.title) document.title = seo.title;
  setMeta("name", "description", seo.meta_description);
  setMeta("name", "robots", seo.robots);
  setCanonical(seo.canonical_url);
  setMeta("property", "og:type", seo.og_type);
  setMeta("property", "og:site_name", siteName);
  setMeta("property", "og:title", seo.og_title);
  setMeta("property", "og:description", seo.og_description);
  setMeta("property", "og:image", seo.og_image);
  setMeta("property", "og:url", seo.canonical_url);
  setMeta("name", "twitter:card", seo.twitter_card);
  setMeta("name", "twitter:title", seo.twitter_title);
  setMeta("name", "twitter:description", seo.twitter_description);
  setMeta("name", "twitter:image", seo.twitter_image);
  setJsonLd(seo.schema);
}

export default function SeoHead() {
  const location = useLocation();
  useEffect(() => {
    const path = location.pathname + (location.search || "");
    let cancelled = false;
    fetchSeoMeta(path)
      .then((data) => { if (!cancelled && data) applySeo(data); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [location.pathname, location.search]);
  return null;
}
